import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  ErrorNotice,
  Field,
  Input,
  Sheet,
  Textarea,
  Toggle,
} from '@/client/components/ui';
import { useSaveRider } from '@/client/store';
import type { Rider } from '@/shared/types';

/**
 * The people who need rides.
 *
 * There is usually one, which is why this lives in a fold of Settings rather
 * than getting a screen of its own. The contact details matter now: the rider's
 * email is what puts them on their own ride invite.
 */
export function RiderRow({ rider, onOpen }: { rider: Rider; onOpen: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="tap-target flex w-full items-center gap-3 rounded-xl px-3 text-left transition-colors hover:bg-surface-sunken"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{rider.name}</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
            {rider.active ? null : <Badge tone="caution">Not active</Badge>}
            {rider.email ? (
              <span className="truncate text-xs text-ink-faint">{rider.email}</span>
            ) : (
              <Badge tone="caution">No email — not on the invites</Badge>
            )}
          </span>
        </span>
      </button>
    </li>
  );
}

export function RiderEditor({ rider, onClose }: { rider: Rider | null; onClose: () => void }) {
  const save = useSaveRider();
  const isNew = rider === null;

  const [draft, setDraft] = useState(() => ({
    name: rider?.name ?? '',
    email: rider?.email ?? '',
    phone: rider?.phone ?? '',
    notes: rider?.notes ?? '',
    active: rider?.active ?? true,
  }));

  // A write queued while offline has nothing left to say until the connection
  // returns, so let the sheet close and let the sync indicator carry it.
  useEffect(() => {
    if (save.isPaused) onClose();
  }, [save.isPaused, onClose]);

  const submit = () => {
    const trimmed = draft.name.trim();
    if (!trimmed) return;

    save.mutate(
      {
        ...(rider?.id ? { id: rider.id } : {}),
        name: trimmed,
        email: draft.email.trim(),
        phone: draft.phone.trim(),
        notes: draft.notes,
        active: draft.active,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={isNew ? 'Add rider' : draft.name || 'Rider'}
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            busy={save.isPending}
            disabled={!draft.name.trim() || save.isPending}
            onClick={submit}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {save.isError ? <ErrorNotice error={save.error} /> : null}

        <Field label="Name">
          {({ id }) => (
            <Input
              id={id}
              value={draft.name}
              autoComplete="name"
              placeholder="Marie"
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          )}
        </Field>

        <Field
          label="Email"
          hint="Optional. With an address here, they get the same calendar invite as the volunteer."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="email"
              inputMode="email"
              autoComplete="email"
              aria-describedby={describedBy}
              value={draft.email}
              onChange={(event) => setDraft({ ...draft, email: event.target.value })}
            />
          )}
        </Field>

        <Field label="Phone" hint="Optional. Used for the prefilled text on a ride.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              aria-describedby={describedBy}
              value={draft.phone}
              onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
            />
          )}
        </Field>

        <Toggle
          label="Active"
          description="Inactive riders are left out of Generate."
          checked={draft.active}
          onChange={(active) => setDraft({ ...draft, active })}
        />

        <Field label="Notes" hint="Anything a driver should know. Goes on the invite.">
          {({ id, describedBy }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              value={draft.notes}
              onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
            />
          )}
        </Field>
      </div>
    </Sheet>
  );
}
